/**
 * Labelled text input built on the `.input` / `.label` component classes with
 * accessible error wiring (`aria-invalid` + `aria-describedby`).
 */
import { forwardRef, useId, type InputHTMLAttributes, type ReactElement } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  /** Extra classes for the outer wrapper (the input keeps `.input`). */
  wrapperClassName?: string;
}

/** Text/number/password input with an optional label and error message. */
const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, id, className = '', wrapperClassName = '', ...rest },
  ref,
): ReactElement {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = `${inputId}-error`;

  return (
    <div className={wrapperClassName}>
      {label && (
        <label htmlFor={inputId} className="label">
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        className={`input ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''} ${className}`.trim()}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        {...rest}
      />
      {error && (
        <p id={errorId} className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
});

export default Input;
